import { FunctionComponent } from 'react'
import { makeStyles } from '@material-ui/styles'
import colors from '../../colors'
import { useAuthStore } from '../../stores/auth'
import { AuthState } from '../../types/auth'
import { BenefitParams, PricingParams } from '../../types'

/**
 * What the patron sees on the VIP membership signup page, rendered live from
 * the creation form.
 *
 * Modelled on vip/pages/memberships/[uuid].tsx - logo, title, org name, price,
 * a WHAT YOU GET list of benefits with interval/quantity pills, then the join
 * button. It is a likeness, not the real page: no gifting, no price toggle
 * behaviour, no referral filtering. Those depend on saved records and would
 * only add ways for this to be subtly wrong.
 *
 * Plain elements rather than MUI ones throughout: @material-ui/core v5-beta
 * (emotion) silently overrides @material-ui/styles v4 (JSS) classes, so sizes
 * and colours set here would not apply to a Typography or Button.
 */

interface MembershipPreviewProps {
  title?: string
  description?: string
  finePrint?: string
  imageUrl?: string
  benefits?: BenefitParams[]
  pricing?: PricingParams[]
}

// Matches BenefitPreviewRow's map in the VIP app, so the wording a brewery
// previews is the wording its patrons read.
const INTERVAL_LABELS: Record<string, string> = {
  once: 'one-time',
  daily: 'daily',
  unlimited: 'unlimited',
  weekly: 'weekly',
  monthly: 'monthly',
  annual: 'annual',
  referral: 'referral bonus',
  periodic: 'periodic',
}

const PRICE_INTERVALS: Record<string, string> = {
  month: '/mo',
  year: '/yr',
  week: '/wk',
  day: '/day',
}

const formatPrice = (price: PricingParams): string => {
  const amount = Number(price.amount)
  if (!amount && amount !== 0) return ''
  // Whole dollars read cleanly; cents only shown when there are any.
  const dollars = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)
  return `$${dollars}${PRICE_INTERVALS[price.interval] ?? ''}`
}

const MembershipPatronPreview: FunctionComponent<MembershipPreviewProps> = ({
  title,
  description,
  finePrint,
  imageUrl,
  benefits = [],
  pricing = [],
}) => {
  const styles = useStyles()
  const orgName = useAuthStore(
    (state: AuthState) => state.user && state.user.organization && state.user.organization.name
  )

  const named = benefits.filter((b) => b.title)
  const priced = pricing.filter((p) => p.amount || p.amount === 0)

  return (
    <div className={styles.wrap}>
      <div className={styles.caption}>Patron preview</div>

      <div className={styles.phone}>
        <div className={styles.screen}>
          <div className={styles.logo}>
            {imageUrl
              ? <img src={imageUrl} alt="" className={styles.logoImg} />
              : <span className={styles.logoFallback}>{(orgName || 'S').charAt(0)}</span>}
          </div>

          <div className={title ? styles.title : styles.titlePlaceholder}>
            {title || 'Your membership name'}
          </div>

          {orgName && <div className={styles.org}>{orgName}</div>}

          {priced.length > 0 && (
            <div className={styles.priceRow}>
              {priced.map((price, i) => (
                <span key={i} className={styles.price}>{formatPrice(price)}</span>
              ))}
            </div>
          )}

          {description && <div className={styles.description}>{description}</div>}

          <div className={styles.benefitsCard}>
            <div className={styles.benefitsHeader}>WHAT YOU GET</div>
            {named.length === 0 ? (
              <div className={styles.empty}>Benefits you add will appear here.</div>
            ) : (
              named.map((benefit, i) => (
                <div
                  key={i}
                  className={i === named.length - 1 ? styles.benefitLast : styles.benefit}
                >
                  <div className={styles.benefitTitle}>{benefit.title}</div>
                  <div className={styles.pills}>
                    {benefit.interval && (
                      <span className={styles.pillBlue}>
                        {INTERVAL_LABELS[benefit.interval] ?? benefit.interval}
                      </span>
                    )}
                    {Number(benefit.quantity) > 1 && (
                      <span className={styles.pillGreen}>{`${benefit.quantity}x`}</span>
                    )}
                    {benefit.restriction === 'annual_only' && (
                      <span className={styles.pillPurple}>Annual Only</span>
                    )}
                  </div>
                  {benefit.details && (
                    <div className={styles.benefitDetails}>{benefit.details}</div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className={styles.join}>Join</div>

          {finePrint && <div className={styles.finePrint}>{finePrint}</div>}

          <div className={styles.poweredBy}>Powered by Stitch</div>
        </div>
      </div>
    </div>
  )
}

const pillBase = {
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '18px',
  height: 18,
  padding: '0 8px',
  borderRadius: 999,
  whiteSpace: 'nowrap' as const,
}

const useStyles = makeStyles({
  wrap: {
    position: 'sticky' as const,
    top: 0,
  },
  caption: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: colors.GREY_3,
    marginBottom: 8,
    textAlign: 'center' as const,
  },
  phone: {
    border: `8px solid ${colors.STITCH_BLACK}`,
    borderRadius: 28,
    background: colors.STITCH_BLACK,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },
  screen: {
    background: colors.GREY_7,
    borderRadius: 20,
    padding: 18,
    // Tall enough to read as a phone even with nothing filled in yet.
    minHeight: 460,
    maxHeight: 620,
    overflowY: 'auto' as const,
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 12,
  },
  logoImg: {
    maxWidth: 72,
    maxHeight: 72,
    objectFit: 'contain' as const,
  },
  logoFallback: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: colors.LIGHT_BLUE,
    color: colors.STITCH_BLUE,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '56px',
    textAlign: 'center' as const,
    textTransform: 'uppercase' as const,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.STITCH_BLUE,
    textAlign: 'center' as const,
    lineHeight: 1.25,
    wordBreak: 'break-word' as const,
  },
  titlePlaceholder: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.GREY_4,
    textAlign: 'center' as const,
    lineHeight: 1.25,
  },
  org: {
    fontSize: 12,
    color: colors.GREY_3,
    textAlign: 'center' as const,
    marginTop: 4,
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
  },
  price: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
    background: colors.WHITE,
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 999,
    padding: '4px 12px',
  },
  description: {
    fontSize: 12,
    color: colors.GREY_2,
    lineHeight: 1.5,
    marginTop: 12,
    // Authors type line breaks into the description and expect to keep them.
    whiteSpace: 'pre-line' as const,
    wordBreak: 'break-word' as const,
  },
  benefitsCard: {
    background: colors.WHITE,
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: 14,
    marginTop: 16,
  },
  benefitsHeader: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    color: colors.GREY_3,
    marginBottom: 10,
  },
  benefit: {
    paddingBottom: 10,
    marginBottom: 10,
    borderBottom: `1px solid ${colors.GREY_6}`,
  },
  benefitLast: {},
  benefitTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.STITCH_BLACK,
    wordBreak: 'break-word' as const,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 4,
    marginTop: 5,
  },
  pillBlue: { ...pillBase, background: colors.PALE_BLUE, color: colors.BRIGHT_BLUE },
  pillGreen: { ...pillBase, background: colors.PALE_GREEN, color: colors.DARK_GREEN },
  pillPurple: { ...pillBase, background: colors.PALE_PURPLE, color: colors.DARK_PURPLE },
  benefitDetails: {
    fontSize: 11,
    color: colors.GREY_2,
    lineHeight: 1.45,
    marginTop: 5,
    whiteSpace: 'pre-line' as const,
    wordBreak: 'break-word' as const,
  },
  empty: {
    fontSize: 12,
    color: colors.GREY_4,
    fontStyle: 'italic' as const,
  },
  join: {
    background: colors.STITCH_BLUE,
    color: colors.WHITE,
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'center' as const,
    borderRadius: 8,
    padding: '12px 0',
    marginTop: 16,
  },
  finePrint: {
    fontSize: 10,
    color: colors.GREY_3,
    lineHeight: 1.45,
    marginTop: 10,
    whiteSpace: 'pre-line' as const,
    wordBreak: 'break-word' as const,
  },
  poweredBy: {
    fontSize: 10,
    color: colors.GREY_4,
    textAlign: 'center' as const,
    marginTop: 12,
  },
})

export default MembershipPatronPreview
